import {MAX_EXAMPLE_VOICE} from '../defines/protocol';
import {IsEmptyString, IsEmptyVar, IsEqualVoiceFile, Txt2Date} from '../defines/common.func';
import {JANGDAN, WORD_SEPARATOR_LINE, WORD_SEPARATOR_NUM} from '../defines/gukeo.const';
import {GetJangDan} from '../defines/gukeo.func';
import {SimpleWord} from './simple-word.model';
import {Proverb} from './proverb.model';
import {SimpleDialect} from './dialect.model';
import {ExampleSJ, ExampleSJList} from './example-sj.model';

export {ExampleSJ, ExampleSJList};

export type ExampleList = Array<Array<string | null> | null>;

export type WordDetailExample = {
  id: string;
  example_s: string;
  example_t: string;
  example_highlight: string | null;
  exampleVoice: string | null;
};


function getHomonym(simpleWordList: Array<object>): Array<SimpleWord> | null {
  const list = new Array<SimpleWord>();
  simpleWordList.forEach(obj => {
    list.push(new SimpleWord(obj));
  });
  return list.length > 0 ? list : null;
}

function getProverb(simpleProverbList: Array<object>): Array<Proverb> | null {
  const list = new Array<Proverb>();
  simpleProverbList.forEach(obj => list.push(new Proverb(obj)));
  return list.length > 0 ? list : null;
}

function getDialect(simpleDialectList: Array<object>): Array<SimpleDialect> | null {
  const list = new Array<SimpleDialect>();
  simpleDialectList.forEach(obj => list.push(new SimpleDialect(obj)));
  return list.length > 0 ? list : null;
}

function getLinkedExamples(examples: Array<any>): Array<WordDetailExample> | null {
  if (!Array.isArray(examples)) return null;

  const out = examples
    .map(example => ({
      id: example?.id ? String(example.id) : '',
      example_s: example?.example_s ? String(example.example_s) : '',
      example_t: example?.example_t ? String(example.example_t) : '',
      example_highlight: example?.example_highlight ? String(example.example_highlight) : null,
      exampleVoice: typeof example?.example_voice === 'string' && !IsEmptyString(example.example_voice)
        ? example.example_voice
        : null,
    }))
    .filter(example =>
      !IsEmptyString(example.id) &&
      !IsEmptyString(example.example_s) &&
      !IsEmptyString(example.example_t)
    );

  return out.length > 0 ? out : null;
}


export class Word extends SimpleWord {
  rhythm: Array<JANGDAN> | null;
  homonyms: Array<SimpleWord> | null;
  proverbs: Array<Proverb> | null;
  dialects: Array<SimpleDialect> | null;
  examples: Array<WordDetailExample> | null;
  meaning3: Array<string> | null;
  example3: ExampleList | null;
  example_sj: ExampleSJList | null;  // 예문 - 성조만

  exampleVoice: Array<string>;
  registered: Date | null;
  lastMod: Date | null;
  lastOp: string;

  bookmark: boolean;

  constructor(obj?: any) {
    super(obj);

    this.rhythm             = (obj && obj.rhythm)             ? GetJangDan(obj.rhythm) : null;
    this.homonyms           = (obj && obj.homonyms)           ? getHomonym(obj.homonyms) : null;
    this.proverbs           = (obj && obj.proverbs)           ? getProverb(obj.proverbs) : null;
    this.dialects           = (obj && obj.dialects)           ? getDialect(obj.dialects) : null;
    this.examples           = (obj && obj.examples)           ? getLinkedExamples(obj.examples) : null;
    this.meaning3           = (obj && obj.meaning3)           ? String(obj.meaning3).split(WORD_SEPARATOR_NUM) : null;
    this.example3           = (obj && obj.example3)           ? this.parseExample3(obj.example3) : null;

    this.exampleVoice       = (obj && obj.example_voices)     ?? null;
    this.example_sj         = (obj && obj.example_t && obj.example_s) ? this.parseExampleSJ(obj.example_s, obj.example_t, obj.example_highlight ? String(obj.example_highlight) : null) : null;

    this.registered         = (obj && obj.registered)         ? Txt2Date(obj.registered) : null;
    this.lastMod            = (obj && obj.last_mod)           ? Txt2Date(obj.last_mod) : null;
    this.lastOp             = (obj && obj.last_op)            ?? null;

    this.bookmark           = (obj && !IsEmptyVar(obj.bookmark)) ? !!obj.bookmark : false;

    // console.log('Word =', this, obj);
  }

  public GetMeaning3Nth(nth: number): string | null {
    const meaning = this.meaning3?.[nth] ?? null;
    return IsEmptyString(meaning) ? null : meaning;
  }

  /**
   * 예문 배열 가지고 오는 함수.
   * (규칙이 복잡함)
   * 뜻이 하나 있는 경우: 첫번째 예문 사용
   * 뜻이 두개 이상 있는 경우 (= 기본 뜻이 있는 경우): 기본 뜻에는 예문이 없다. 그래서 (예문 갯수 === 뜻 갯수 - 1)
   * @param nth
   * @constructor
   * @protected
   */
  public GetExample3Nth(nth: number): Array<string | null> | null {
    if (this.exampleLen > 1) {
      // 뜻이 두개 이상인 경우
      // 뜻 0번은 기본 뜻이라서 예문이 없다.
      // 뜻 1번부터 예문이 있어서 -1을 해줌.
      return nth === 0 ? null : this.example3?.[nth - 1] ?? null;
    }
    else {
      return this.example3?.[nth] ?? null;
    }
  }

  public GetExampleSjNth(nth: number): Array<ExampleSJ | null> | null {
    if (this.exampleLen > 1) {
      // 뜻이 두개 이상인 경우
      // 뜻 0번은 기본 뜻이라서 예문이 없다.
      // 뜻 1번부터 예문이 있어서 -1을 해줌.
      return nth === 0 ? null : this.example_sj?.[nth - 1] ?? null;
    }
    else {
      return this.example_sj?.[nth] ?? null;
    }
  }

  /**
   * 특정 예문 번호와 문장 번호에 해당하는 ExampleSJ 객체를 반환합니다.
   * @param nth - 예문 번호
   * @param ex - 예문 번호 안의 문장 번호
   * @constructor
   */
  public GetExampleSjNth2(nth: number, ex: number): ExampleSJ | null {
    const exampleSj = this.GetExampleSjNth(nth);
    if (!exampleSj) return null;

    return exampleSj[ex];
  }

  // public get derivativeTxt(): string {
  //   return (this.derivative && Array.isArray(this.derivative)) ? this.derivative.join(', ') : '';
  // }

  public HaveExVoiceNth(n: number): boolean {
    return (
      this.exampleVoice &&
      this.exampleVoice.length > n &&
      !IsEmptyString(this.exampleVoice[n]));
  }

  public override get hasVoice(): boolean {
    return !IsEmptyString(this.wordVoice) || this.exampleVoice?.some(v => !IsEmptyString(v));
  }

  /**
   * 예문 번호와 문장 번호에 해당하는 음성 데이터를 반환합니다.
   * @param midx - 뜻 번호 (예문 번호가 아님)
   *              0 = 기본 뜻에 붙은 예문
   *              1,2,3 = 1,2,3번때 뜻에 붙은 예문
   * @param idx - 문장 번호 (0부터 시작)
   * @constructor
   */
  public GetExVoice(midx: number, idx: number): string | null {
    if (midx == 0) ++midx;  // 예문 번호가 0인 경우 예문은 1번을 사용한다.

    const voiceFile = this.id + '-' + midx + '-' + (idx + 1);  // idx + 1: 예문 파일명은 1부터 시작

    const matchFile = this.exampleVoice?.find(v => IsEqualVoiceFile(v, voiceFile)) ?? null;
    return matchFile;
  }

  /**
   * 예문 번호와 문장 번호에 해당하는 음성 데이터의 인덱스를 반환합니다.
   * @param midx - 뜻 번호 (예문 번호가 아님)
   *              0 = 기본 뜻에 붙은 예문
   *              1,2,3 = 1,2,3번때 뜻에 붙은 예문
   * @param idx - 문장 번호 (0부터 시작)
   * @return    0 이상: 음성 데이터의 인덱스
   *            -1: 음성 데이터가 없음
   * @constructor
   */
  public GetExVoiceIdx(midx: number, idx: number): number {
    if (midx == 0) ++midx;  // 예문 번호가 0인 경우 예문은 1번을 사용한다.

    const voiceFile = this.id + '-' + midx + '-' + (idx + 1);  // idx + 1: 예문 파일명은 1부터 시작
    // console.warn('GetExVoiceIdx', num, idx, this.meaning3?.length ?? 0, voiceFile);

    const matchIdx = this.exampleVoice?.findIndex(v => IsEqualVoiceFile(v, voiceFile)) ?? null;
    return matchIdx;
  }

  /**
   * 예문을 파싱하는 함수
   *
   * NOTE: /를 줄넘김 기호로 사용하는데 html tag 안에 있으면 줄넘김 기호로 인식하지 않아야 함.
   *
   * @param example3 - 예문 데이터
   * @returns - 예문 데이터
   */
  private parseExample3(example3: string): ExampleList | null {
    if (!example3) return null;

    return example3.split(WORD_SEPARATOR_NUM).map(example =>
      !IsEmptyString(example) ?
        example.split(/(?![^<]*>)\//)
          .map(ex => !IsEmptyString(ex) ? ex : null)
          .filter(ex => ex !== null)
        : null
    );
  }

  /**
   * 성조 포함한 예문을 파싱하는 함수
   * @param example_s - 예문의 성조 데이터
   * @param example_t - 예문의 문장 데이터
   * @param example_highlight - 예문의 글자 강조 데이터(example_t와 동일 구조). 없으면 null.
   * @private
   *
   * NOTE: 이 함수를 호출하기 전에 this.exampleVoice를 먼저 설정해야 함.
   */
  private parseExampleSJ(example_s: string, example_t: string, example_highlight: string | null): ExampleSJList | null {
    if (!example_s || !example_t) return null;

    const seungjos = example_s.split(WORD_SEPARATOR_NUM).map(seungjo =>
      !IsEmptyString(seungjo) ? seungjo.split(WORD_SEPARATOR_LINE).map(sj => !IsEmptyString(sj) ? sj : null) : null
    );
    const examples = example_t.split(WORD_SEPARATOR_NUM).map(example =>
      !IsEmptyString(example) ? example.split(WORD_SEPARATOR_LINE).map(ex => !IsEmptyString(ex) ? ex : null) : null
    );
    // 강조 데이터는 example_t와 동일하게 분할한다. 누락 시 null로 두어 강조만 생략한다.
    const highlights = example_highlight
      ? example_highlight.split(WORD_SEPARATOR_NUM).map(highlight =>
        !IsEmptyString(highlight) ? highlight.split(WORD_SEPARATOR_LINE).map(hl => !IsEmptyString(hl) ? hl : null) : null
      )
      : null;

    return examples.map((example, num) => {
      // num = 예문 번호
      if (!example) return null;

      const midx = this.exampleLen > 1 ? num + 1 : num;  // 뜻 번호 계산

      return example
        .filter(ex => ex !== null) // null인 항목을 제거합니다.
        .map((ex, i) => ex ? {
          // i = 예문 내의 문장 번호
          seungjo: seungjos[num]?.[i] ?? null,
          example: ex,
          voiceExIdx: (() => {
            const idx = this.GetExVoiceIdx(midx, i);
            return idx === -1 ? null : idx;
          })(),
          highlight: highlights?.[num]?.[i] ?? null,
        } : null
      );
    });
  }

  private get exampleLen(): number {
    return this.meaning3?.length ?? 0;
  }
}
